import { Link, useRouterState } from '@tanstack/react-router';

import type { ComponentType } from 'react';

import { cn } from '@/lib/utils/cn';

export interface BottomNavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Render a plain <a href> (same-page anchors / cross-surface links) instead of <Link>. */
  external?: boolean;
}

type Surface = 'editorial' | 'video';

// One component, two palettes (the pattern JoinCta uses). Default 'editorial'
// keeps the member/creator/admin shells byte-identical (Spec 32 confirm 1).
const surfaceClasses: Record<Surface, { nav: string; active: string; idle: string }> = {
  editorial: { nav: 'border-ink/10 bg-parchment', active: 'text-oxblood', idle: 'text-ink/60' },
  video: {
    nav: 'border-video-line bg-video-panel',
    active: 'text-oxblood-live',
    idle: 'text-video-text-muted',
  },
};

export function BottomNav({
  items,
  homeHref,
  surface = 'editorial',
}: {
  items: BottomNavItem[];
  homeHref: string;
  surface?: Surface;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const s = surfaceClasses[surface];
  return (
    <nav
      aria-label="Primary mobile"
      className={cn(
        'fixed inset-x-0 bottom-0 z-30 flex border-t pb-[env(safe-area-inset-bottom)] md:hidden',
        s.nav,
      )}
    >
      {items.map((item) => {
        // The layout root (== homeHref) matches exactly; everything else
        // prefix-matches so child routes keep their parent active. External
        // items (anchors / cross-surface) are never "active".
        const active =
          !item.external &&
          (item.to === homeHref
            ? pathname === item.to
            : pathname === item.to || pathname.startsWith(item.to + '/'));
        const Icon = item.icon;
        const cls = cn(
          'flex flex-1 flex-col items-center gap-1 py-2 text-[0.7rem] font-medium uppercase tracking-[0.08em]',
          active ? s.active : s.idle,
        );
        const inner = (
          <>
            <Icon className="h-5 w-5" />
            <span>{item.label}</span>
          </>
        );
        return item.external ? (
          <a key={item.to} href={item.to} className={cls}>
            {inner}
          </a>
        ) : (
          <Link
            key={item.to}
            to={item.to}
            className={cls}
            aria-current={active ? 'page' : undefined}
          >
            {inner}
          </Link>
        );
      })}
    </nav>
  );
}
